import { useRef } from 'react';

import getEnvironmentVariables from '@/config/environment';
import { RoomConnection } from '@/types/bentoStream';
import createWebSocket from './createWebsocket';
import { useLiveCheckContext } from '@/contexts/LiveCheckContext';

const useWebSocket = (): RoomConnection => {
  const { ENABLE_BENTO_STREAM } = getEnvironmentVariables();

  const subscribersRef = useRef<{
    [id: string]: {
      channel: string;
      callback: (data: Object) => any | Promise<any>;
    };
  }>({});

  let _webSocket: WebSocket | null = null;

  const _onMessage = async (event: MessageEvent): Promise<void> => {
    const data = JSON.parse(event.data);
    const { channel } = data;

    if (!channel) {
      return;
    }

    Object.keys(subscribersRef.current).forEach(async subscriberId => {
      const subscriber = subscribersRef.current[subscriberId];
      if (subscriber.channel === channel) {
        await subscriber.callback(data.object);
      }
    });
  };

  const connect = async (billId: string): Promise<void> => {
    if (ENABLE_BENTO_STREAM) {
      _webSocket = await createWebSocket(_onMessage, billId);
    }
  };

  const subscribe = (
    channel: string,
    callback: (data: Object) => any | Promise<any>
  ): string => {
    // assumes only one callback can exist per channel
    const subscriberId = channel;

    subscribersRef.current = {
      ...subscribersRef.current,
      [subscriberId]: { callback, channel }
    };

    return subscriberId;
  };

  const unsubscribe = (subscriberId: string): void => {
    const subscribers = { ...subscribersRef.current };
    delete subscribers[subscriberId];

    subscribersRef.current = { ...subscribers };
  };

  const _unsubscribeFromAll = (): void => {
    const subscriberIds = Object.keys(subscribersRef.current);
    subscriberIds.forEach(id => unsubscribe(id));
  };

  const disconnect = (): void => {
    _unsubscribeFromAll();

    if (_webSocket) {
      _webSocket.close();
    }

    _webSocket = null;
  };

  return { connect, subscribe, unsubscribe, disconnect, subscribersRef };
};

export default useWebSocket;
